import {isAbortError} from "../api/executor";
import {IndicationValidationError, ValueIndicationApiInterface} from "../api/interface/value-indication";
import type {ValueIndicationRequest} from "../api/payload-types/value-indication";
import {generateDefaultQueryError} from "../util/errorUtil";
import {RevaPermission} from "../util/revaPermission";
import {selectPermissionCheck} from "./authSelectors";
import {selectCredentials} from "./authSlice";
import type {IndicationRequestResult} from "./indicationRequests";
import {selectCurrentIndicationPayload} from "./indicationSelectors";
import {initStandardGroundValueDocPropertiesAsync} from "./locationInfoThunk";
import {setExternalValidationErrors} from "./objectParametersSlice";
import {selectReadOnly, selectRevaId} from "./persistenceSlice";
import type {RevaThunkAction} from "./revaHooks";
import type {RevaExtraArgs} from "./store";
import {
    clearLoading,
    setIndicationData,
    setLoading,
    setMissingPermissions,
    setQueryError,
} from "./valueIndicationSlice";

// AbortController der zuletzt gestarteten Wert-Indikations-Anfrage: eine neue Anfrage bricht eine noch laufende
// vorige ab, damit eine überholte (spät eintreffende) Antwort nie den aktuelleren Stand in Store und GUI
// überschreibt — sonst zeigt die GUI einen Wert zu veralteten Parametern, und Finalize friert ihn ein (#19372).
// Ein globaler Guard genügt, da immer nur eine Bewertung aktiv im Store geladen ist.
let valueIndicationAbortController: AbortController | null = null;

/**
 * Bricht die aktuell laufende Wert-Indikation ab, ohne eine neue zu starten (#19374).
 * Für Stellen, an denen der Bezug der Anfrage wegfällt (andere Bewertung geladen, Adresse entfernt,
 * Objektparameter zurückgesetzt). Meist über {@link abortRunningIndications} aufgerufen.
 */
export const abortValueIndication = (): void => {
    valueIndicationAbortController?.abort();
    valueIndicationAbortController = null;
};

/**
 * Async thunk to run a ValueIndication request.
 * @param payload JSON payload, including address, segment and all params
 */
export const queryValueIndicationAsync =
    (payload: ValueIndicationRequest): RevaThunkAction<Promise<IndicationRequestResult>> =>
    async (dispatch, getState, {env, serviceApi}: RevaExtraArgs) => {
        const readOnly = selectReadOnly(getState());

        if (readOnly) {
            // Die Bewertung ist eingefroren (Snapshot oder finalisiert): eine noch laufende Anfrage der
            // vorigen, bearbeitbaren Bewertung darf die eingefrorenen Werte nicht überschreiben (#19374).
            abortValueIndication();
            return "skipped";
        }

        if (!payload) {
            return "skipped";
        }
        const revaId = selectRevaId(getState());
        if (!selectPermissionCheck(RevaPermission.INDICATION_VALUE)(getState())) {
            if (env.isDevelopmentMode) {
                console.log("Skipping loading of Value Indication due to missing permissions");
            }
            dispatch(setMissingPermissions());
            return "skipped";
        }
        const controller = new AbortController();
        const apiInterface = new ValueIndicationApiInterface(revaId, payload, true, true, controller.signal);
        if (!apiInterface.validateAndDispatchQueryError((queryError) => dispatch(setQueryError(queryError)))) {
            return "error";
        }

        // Erst NACH erfolgreicher Validierung die vorherige (evtl. noch laufende) Anfrage abbrechen — eine neue,
        // aber ungültige Anfrage darf eine noch laufende gültige nicht canceln (#19372).
        valueIndicationAbortController?.abort();
        valueIndicationAbortController = controller;

        dispatch(setLoading());
        const credentials = selectCredentials(getState());
        try {
            if (env.isDevelopmentMode) {
                const {address, segment} = payload;
                console.log(`Value Indication query for address: ${address} and segment ${segment}`, payload);
            }
            const valueIndicationData = await apiInterface.execute(serviceApi, credentials!);
            // Zweite Absicherung neben dem AbortSignal: Wurde inzwischen eine neuere Anfrage gestartet oder
            // die Bewertung gewechselt, gehört diese Antwort nicht mehr zum aktuellen Stand (#19374).
            if (valueIndicationAbortController !== controller) {
                return "aborted";
            }
            dispatch(
                setIndicationData({
                    requestData: payload,
                    indicationData: valueIndicationData,
                }),
            );
            dispatch(initStandardGroundValueDocPropertiesAsync());
            return "done";
        } catch (err) {
            // Von einer neueren Anfrage überholt und abgebrochen — erwartbar, still schlucken: die neuere Anfrage
            // hat den Store bereits aktualisiert bzw. tut es gleich. Muss vor IndicationValidationError geprüft
            // werden, damit eine abgebrochene Anfrage weder Fehler setzt noch die neuere Antwort löscht (#19372).
            if (isAbortError(err)) {
                return "aborted";
            }
            if (err instanceof IndicationValidationError) {
                dispatch(
                    setExternalValidationErrors({
                        externalValidationErrors: err.getExternalValidationErrors(),
                    }),
                );
                dispatch(clearLoading());
            } else {
                dispatch(setQueryError(generateDefaultQueryError(err as Error)));
            }
            return "error";
        }
    };

/**
 * Async thunk to run a ValueIndication request with the params currently held in the store.
 */
export const queryValueIndicationWithCurrentParamsAsync =
    (): RevaThunkAction<Promise<IndicationRequestResult>> => async (dispatch, getState) => {
        const payload = selectCurrentIndicationPayload(getState());
        if (!payload) {
            return "skipped";
        }

        return await dispatch(queryValueIndicationAsync(payload));
    };
